import { BirthWishViewModel } from '../ViewModels';
import {
    ActionResponse,
    ActionStatusHelper,
    DomainTableConfig,
    useLayoutLifeCycle,
} from '@/Shared';

import { BirthWishHeader } from '../birth-wish-config';
import { useBirthWishEnricher } from '../Hooks';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useBirthWish } from '@/OneLiners';
import { BirthWishModel } from '../BirthWishModel';

/**
 * useBirthWishTableConfig hook
 *
 * Domain-specific layout composition hook for BirthWish entities.
 * Acts as the composition layer between:
 * - BirthWish domain data access,
 * - enrichment logic and relationship resolution,
 * - permission state aggregation,
 * - and the generic layout lifecycle infrastructure.
 *
 * Responsibilities:
 * - Combine domain and enrichment readiness states into a single contract.
 * - Provide dynamic header configuration for the BirthWish table.
 * - Define strict Model → ViewModel transformation.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * This hook is declarative by design.
 * It specifies domain dependencies and transformations,
 * while lifecycle mechanics remain centralized in infrastructure.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<BirthWishViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<BirthWishViewModel>
 */
export function useBirthWishTableConfig(props: DomainHookProps): DomainTableConfig<BirthWishViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.BirthWish;

    /**
     * BirthWish domain entry point.
     */
    const birthWishHook = useBirthWish();

    /**
     * BirthWish enrichment layer.
     *
     * Responsible for resolving user and contact relationships
     * and computing derived BirthWish fields.
     */
    const {
        userMap,
        contactMap,
        getStrictEnrichedBirthWish,
        enricherResponse,
    } = useBirthWishEnricher();

    /**
    * Combined readiness response.
    *
    * The layout lifecycle will only initialize once
    * this contract reaches a succeeded state.
    */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            birthWishHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: birthWishHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
    * Delegation to generic layout lifecycle.
    */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: birthWishHook,

        /**
         * Header configuration for Property table.
         */
        headers: BirthWishHeader.build(contactMap),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            birthWish: BirthWishModel, isDisplaying: boolean) =>
            new BirthWishViewModel({
                birthWish: getStrictEnrichedBirthWish(birthWish),
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}